/**
 * ModeSegmentedControl Component - TopBar MVP
 *
 * @version 1.0
 * @updated 2026-04-11
 * @module core/layouts/components
 *
 * @description
 * Control segmentado para selección de modalidad operacional.
 * Reemplaza los 5 botones separados con un segmented control compacto.
 *
 * @remarks
 * **Modos operacionales**:
 * - Explorar (E): Navegación y consulta read-only
 * - Inventariar (I): Crear y editar elementos
 * - Analizar (A): Análisis de indicadores
 * - Planificar (P): Escenarios what-if
 * - Operar (O): Gestión operativa (futuro)
 *
 * **Comportamiento responsive**:
 * - ≥1440px: Labels completos
 * - <1440px: Solo iniciales con tooltips
 */

import { useState } from 'react';
import { useOperationalModeStore } from '../../operational-modes';
import type { OperationalMode } from '../../operational-modes';
import { useEditLock } from '../EditLockContext';
import './mode-segmented.css';

/**
 * Iconos SVG para modos (compactos para segmented)
 */
const MODE_ICONS: Record<OperationalMode, JSX.Element> = {
  explore: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  inventory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  analyze: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  ),
  plan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="2" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  ),
  operate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  ),
};

/**
 * Definición de modos con colores
 */
interface ModeDefinition {
  id: OperationalMode;
  name: string;
  shortName: string;
  initial: string;
  description: string;
  color: string;
}

const MODE_DEFINITIONS: ModeDefinition[] = [
  { id: 'explore', name: 'Explorar', shortName: 'Expl', initial: 'E', description: 'Navegación libre y consulta de elementos', color: '#FF8C42' },
  { id: 'inventory', name: 'Inventariar', shortName: 'Inv', initial: 'I', description: 'Crear, editar y mantener el catastro', color: '#4ADE80' },
  { id: 'analyze', name: 'Analizar', shortName: 'Anal', initial: 'A', description: 'Análisis de indicadores y selección zonal', color: '#3B82F6' },
  { id: 'plan', name: 'Planificar', shortName: 'Plan', initial: 'P', description: 'Diseñar intervenciones y simular escenarios', color: '#E879F9' },
  { id: 'operate', name: 'Operar', shortName: 'Oper', initial: 'O', description: 'Programación y seguimiento operativo', color: '#6B7280' },
];

/**
 * Props del ModeSegmentedControl
 */
export interface ModeSegmentedControlProps {
  /** Mostrar labels completos en vez de iniciales */
  showLabels?: boolean;
  /** Mostrar iconos junto al texto */
  showIcons?: boolean;
  /** Callback cuando cambia el modo */
  onModeChange?: (mode: OperationalMode) => void;
  /** Modos visibles. Si no se pasa, muestra todos. */
  visibleModes?: OperationalMode[];
}

/**
 * Componente ModeSegmentedControl
 */
export function ModeSegmentedControl({
  showLabels = false,
  showIcons = false,
  onModeChange,
  visibleModes,
}: ModeSegmentedControlProps): JSX.Element {
  const { mode: activeMode, setMode } = useOperationalModeStore();
  const { isEditingActive, hasUnsavedChanges } = useEditLock();
  const [hoveredMode, setHoveredMode] = useState<OperationalMode | null>(null);

  const modes = visibleModes
    ? MODE_DEFINITIONS.filter(m => visibleModes.includes(m.id))
    : MODE_DEFINITIONS;

  /**
   * Maneja click en un segmento
   */
  const handleModeClick = (modeId: OperationalMode): void => {
    // Bloqueo si hay edición activa
    if (isEditingActive && hasUnsavedChanges) {
      alert('Debe guardar o cancelar los cambios antes de cambiar de modo.');
      return;
    }

    // Si es el mismo modo, volver a explore (excepto si ya es explore)
    if (activeMode === modeId && modeId !== 'explore') {
      setMode('explore');
      onModeChange?.('explore');
      return;
    }

    setMode(modeId);
    onModeChange?.(modeId);
  };

  /**
   * Obtiene el modo activo actual
   */
  const activeModeData = MODE_DEFINITIONS.find(m => m.id === activeMode);

  return (
    <div className="mode-segmented-container">
      <nav
        className={`mode-segmented ${showLabels ? 'with-labels' : ''}`}
        role="tablist"
        aria-label="Modos operacionales"
      >
        {modes.map((mode) => {
          const isActive = activeMode === mode.id;
          const isDisabled = isEditingActive && hasUnsavedChanges && !isActive;

          return (
            <button
              key={mode.id}
              type="button"
              role="tab"
              className={`mode-segment ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => handleModeClick(mode.id)}
              onMouseEnter={() => setHoveredMode(mode.id)}
              onMouseLeave={() => setHoveredMode(null)}
              disabled={isDisabled}
              aria-selected={isActive}
              aria-label={mode.description}
              title={mode.description}
              data-mode={mode.id}
              style={{
                '--mode-color': mode.color,
                '--mode-color-alpha': `${mode.color}33`,
              } as React.CSSProperties}
            >
              {showIcons && (
                <span className="mode-segment-icon">
                  {MODE_ICONS[mode.id]}
                </span>
              )}
              <span className="mode-segment-label">
                {showLabels ? mode.shortName : mode.initial}
              </span>
            </button>
          );
        })}

        {/* Indicador deslizante */}
        <div
          className="mode-segment-indicator"
          style={{
            '--indicator-index': modes.findIndex(m => m.id === activeMode),
            '--indicator-color': activeModeData?.color || '#FF8C42',
          } as React.CSSProperties}
        />
      </nav>

      {/* Tooltip del modo en hover: nombre + descripción + color del modo */}
      {hoveredMode && (() => {
        const hovered = MODE_DEFINITIONS.find(m => m.id === hoveredMode);
        return (
          <div
            className="mode-segment-tooltip"
            style={{ '--tooltip-mode-color': hovered?.color } as React.CSSProperties}
          >
            <span
              className="mode-segment-tooltip-name"
              style={{ color: hovered?.color }}
            >
              {hovered?.name}
            </span>
            <span className="mode-segment-tooltip-desc">
              {hovered?.description}
            </span>
          </div>
        );
      })()}
    </div>
  );
}
